import React from 'react';
import { X, Plus, Minus, ShoppingBag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../contexts/CartContext';
import { formatPrice } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../ui/sheet';

export default function CartDrawer() {
  const { t } = useTranslation();
  const { cart, isOpen, closeCart, updateItem, removeItem, loading } = useCart();

  const items = cart?.items || [];
  const isEmpty = items.length === 0;

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">{t('cart.title')}</SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-foreground"></div>
          </div>
        ) : isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <ShoppingBag className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground mb-6">{t('cart.empty')}</p>
            <Button onClick={closeCart} asChild>
              <Link to="/looks">{t('cart.continue')}</Link>
            </Button>
          </div>
        ) : (
          <>
            {/* Items */}
            <div className="flex-1 overflow-auto py-4">
              <AnimatePresence>
                {items.map((item) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className="flex gap-4 py-4 border-b border-border"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Image */}
                    <div className="w-20 h-24 bg-muted overflow-hidden flex-shrink-0">
                      <img
                        src={
                          item.look?.images?.[0] || 
                          item.product?.images?.[0] || 
                          'https://images.unsplash.com/photo-1652281846260-14c1bdd5e9a0?w=200'
                        }
                        alt={item.look?.name || item.product?.name || 'Item'}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium truncate">
                        {item.look?.name || item.product?.name || 'Item'}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {formatPrice(item.price_cents)}
                      </p>

                      {/* Quantity */}
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateItem(item.id, Math.max(1, item.quantity - 1))}
                          data-testid={`decrease-qty-${item.id}`}
                        >
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-8 text-center text-sm">{item.quantity}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => updateItem(item.id, item.quantity + 1)}
                          data-testid={`increase-qty-${item.id}`}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Remove */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 flex-shrink-0"
                      onClick={() => removeItem(item.id)}
                      data-testid={`remove-item-${item.id}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Summary */}
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t('cart.subtotal')}</span>
                <span>{formatPrice(cart.subtotal_cents)}</span>
              </div>
              
              {cart.shipping_cents > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('cart.shipping')}</span>
                  <span>{formatPrice(cart.shipping_cents)}</span>
                </div>
              )}
              
              {cart.discount_cents > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t('cart.discount')}</span>
                  <span>-{formatPrice(cart.discount_cents)}</span>
                </div>
              )}

              <div className="flex justify-between text-lg font-medium pt-2 border-t border-border">
                <span>{t('cart.total')}</span>
                <span>{formatPrice(cart.total_cents)}</span>
              </div>

              <Button 
                className="w-full mt-4" 
                size="lg"
                onClick={closeCart}
                asChild
                data-testid="checkout-button"
              >
                <Link to="/checkout">{t('cart.checkout')}</Link>
              </Button>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={closeCart}
                asChild
              >
                <Link to="/looks">{t('cart.continue')}</Link>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
